import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Reveal, Eyebrow, ArrowButton, ChipFlow } from "@/components/bits";
import { RESEARCH_AREAS, GRANT_CAPABILITIES } from "@/data/content";

const AI_AREAS = [
  {
    title: "AI-Enabled Nutrition",
    kicker: "Making nutrition more personalised",
    body: "AI can help transform nutrition from static information into experiences that respond to an individual's food choices, health goals, symptoms, clinical context, behavioural patterns and health data. TNN Africa has explored this through products such as PlateMind, where AI is used to analyse food, interpret nutrition information and provide personalised insights for people managing diabetes.",
    link: { label: "Example: PlateMind", to: "/our-work/platemind", testid: "ai-area-platemind" },
  },
  {
    title: "Clinical Decision Support",
    kicker: "Turning clinical knowledge into intelligent tools",
    body: "Healthcare professionals work with increasingly complex information. TNN Africa explores how digital products and AI can help translate clinical knowledge into structured, usable decision-support experiences across clinical workflows, nutrition assessment, patient data, evidence-based recommendations and human oversight. The goal is not to replace clinicians, but to build tools that help clinicians and patients make better-informed decisions.",
    link: { label: "Example: Lana Clinical", to: "/our-work/lana-clinical", testid: "ai-area-lana" },
  },
  {
    title: "Nutrition Intelligence",
    kicker: "Making food data useful",
    body: "Nutrition information is often fragmented across food composition databases, recipes, clinical guidelines and consumer-facing products. TNN Africa works on translating this information into structured nutrition intelligence that can power digital products: from food composition and recipe data to nutrient analysis, meal intelligence and personalised insight. PlateMind is an example of how this nutrition intelligence becomes part of a consumer-facing digital health experience.",
    link: null,
  },
  {
    title: "Digital Health & Patient Experience",
    kicker: "Moving nutrition beyond the consultation room",
    body: "Many nutrition interventions are still delivered through episodic consultations. TNN Africa has explored digital models that extend support beyond the clinic through digital nutrition programmes, patient-facing platforms, personalised guidance, symptom-aware support, health tracking, digital education and ongoing engagement. This work spans products such as Sito Health, OncoNourish and PlateMind.",
    link: null,
  },
];

const INNOVATION_PROJECTS = [
  {
    name: "PlateMind",
    tags: "AI × Nutrition × Diabetes",
    desc: "AI-powered nutrition intelligence and decision support for everyday diabetes management.",
    to: "/our-work/platemind",
    testid: "innovation-platemind",
  },
  {
    name: "OncoNourish",
    tags: "Clinical Nutrition × Digital Care × Oncology",
    desc: "A patient-centred approach to structured nutrition support during cancer treatment.",
    to: "/our-work/onconourish",
    testid: "innovation-onconourish",
  },
  {
    name: "Lana Clinical",
    tags: "AI × Healthcare × Product Innovation",
    desc: "An AI-enabled healthcare product developed through the Build the Future of Healthcare Hackathon in London.",
    to: "/our-work/lana-clinical",
    testid: "innovation-lana",
  },
];

export default function Research() {
  return (
    <main data-testid="research-page">
      <PageHero
        testid="research-hero"
        eyebrow="Research & Innovation"
        title={
          <>
            Building evidence for <span className="text-tnn-purple">better health.</span>
          </>
        }
        lead="TNN Africa works at the intersection of nutrition science, healthcare innovation and technology to develop practical solutions for real-world health challenges."
      />

      <section className="bg-white py-16 lg:py-24" data-testid="research-areas">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Research</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {RESEARCH_AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <div
                  data-testid={`research-area-${i}`}
                  className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-7 h-full transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <span className="w-8 h-8 rounded-full bg-tnn-soft text-tnn-purple text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-tnn-ink">{a.title}</h3>
                  <p className="mt-2 text-sm text-tnn-muted leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="ai-health-tech">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>AI & Health Technology</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-2xl leading-[1.15]">
              Building the next generation of nutrition and healthcare products.
            </h2>
            <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed max-w-2xl">
              TNN Africa sits at the intersection of clinical expertise, product development and emerging technology. Our
              technology work focuses on translating real healthcare problems into digital products that make nutrition
              and healthcare more personalised, accessible and actionable. Technology is used to solve specific problems,
              never as an end in itself.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {AI_AREAS.map((area, i) => (
              <Reveal key={area.title} delay={i * 0.06}>
                <div
                  data-testid={`ai-area-${i}`}
                  className="bg-white border border-tnn-ink/6 rounded-3xl p-8 h-full transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple">{area.kicker}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold text-tnn-ink">{area.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-tnn-muted">{area.body}</p>
                  {area.link && (
                    <Link
                      to={area.link.to}
                      data-testid={area.link.testid}
                      className="group mt-5 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.14em] uppercase text-tnn-purple"
                    >
                      {area.link.label}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="innovation-projects">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Selected Innovation Projects</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {INNOVATION_PROJECTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <Link
                  to={p.to}
                  data-testid={p.testid}
                  className="group block bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 h-full transition-colors duration-300 hover:bg-tnn-purple"
                >
                  <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-tnn-purple group-hover:text-tnn-lilac transition-colors duration-300">
                    {p.tags}
                  </p>
                  <h3 className="mt-3 font-display text-xl font-semibold text-tnn-ink group-hover:text-white transition-colors duration-300">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-tnn-muted group-hover:text-white/75 leading-relaxed transition-colors duration-300">
                    {p.desc}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.14em] uppercase text-tnn-purple group-hover:text-white transition-colors duration-300">
                    Explore
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div data-testid="our-principle" className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 lg:p-12">
              <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-tnn-ink leading-[1.2] max-w-2xl">
                Technology should start with the healthcare problem, <span className="text-tnn-purple">not the technology.</span>
              </p>
              <div className="mt-7">
                <ChipFlow items={["Clinical expertise", "Product thinking", "Technology", "Real-world use"]} separator="→" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="grants-consortium">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <Eyebrow>Grants & Consortium Partnerships</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                From research to implementation.
              </h2>
              <p className="mt-5 text-[15px] text-tnn-muted leading-relaxed">
                TNN Africa works with research institutions, implementation partners and organisations to translate
                nutrition science into practical programmes, digital solutions and healthcare innovations.
              </p>
              <div className="mt-8">
                <ArrowButton to="/contact" testid="grants-cta">Discuss a Consortium Partnership</ArrowButton>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="flex flex-wrap gap-2.5">
                {GRANT_CAPABILITIES.map((c, i) => (
                  <span
                    key={c}
                    data-testid={`grant-capability-${i}`}
                    className="text-xs font-semibold px-4 py-2.5 rounded-full bg-white border border-tnn-ink/8 text-tnn-purple transition-colors duration-200 hover:bg-tnn-purple hover:text-white cursor-default"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
